import {FACTIONS} from '../constants/factions';
import {UNIT_TYPES} from './units';

/**
 * СЛЕДЫ ЗА ИДУЩИМИ ОТРЯДАМИ. Отряд выжигает золотые отпечатки: тем же золотым светом
 * горят контуры отрядов ходящего игрока и крылья летающих, поэтому след виден на любой
 * подложке и среди цветных жетонов и не зависит от цвета игрока. Отпечаток ложится
 * раскаленным добела, садится на золото, а потом остывает волной от начала пути к концу
 * и рассыпается темной подпалиной. Это только вид: на правила перемещения следы не влияют.
 *
 * Чем крупнее отряд, тем больше идут рядом (FOOTPRINT_WALKER_STEPS). Чем сильнее тип
 * отряда, тем богаче след (FOOTPRINT_TIERS): у сильных свет идет с отрядом, по пути тянется
 * огненная полоса, земля трескается, на приходе бьет волна.
 * Вид отпечатка и знаки, вылетающие из-под ног, задает облик отряда
 * (FOOTPRINT_FACTION_STYLES): отряд общим силуэтом оставляет обычные сапоги и подковы.
 *
 * Летающие отряды следов не оставляют: у них свой полет (см. wingedFlightAnimation).
 */

// Отпечатки и частицы. Порядок повторен в scripts/generateUnitFootprints.js — его сверяет
// тест конфига. Меняется порядок — меняется и номер в имени файла: сервер кеширует
// картинки на сутки. За ними в листе идут размытые силуэты отпечатков: «<имя>_glow»
export const FOOTPRINT_PRINT_FRAMES = [
  'boot', 'sabaton', 'hobnail', 'curly', 'talon', 'cloven', 'boar', 'flame',
  'leaf_sole', 'bare', 'giant', 'orc_foot', 'goblin_foot', 'spike', 'rat_paw', 'stiletto',
  'webbed', 'bone_foot', 'root', 'blob', 'horseshoe', 'war_shoe', 'flame_shoe', 'wolf_paw',
  'demon_talon', 'dragon_talon', 'hell_shoe', 'rut', 'tail', 'wisp',
];
export const FOOTPRINT_EFFECT_FRAMES = [
  'crack', 'leaf', 'clover', 'heart', 'drop', 'spore', 'coin', 'bone',
  'dust', 'glow', 'ring', 'hex_ring', 'rays', 'streak', 'tongue', 'splinter',
  'chip', 'fang', 'gouge', 'miasma_a', 'miasma_b', 'chevron', 'fissure',
];

// Картинки собирает npm run generate:unit-footprints
export const FOOTPRINT_SHEET = {
  key: 'unit_footprints',
  path: '/img/effects/footprints/footprints.v1.png',
  frameSize: 96,
  frames: [
    ...FOOTPRINT_PRINT_FRAMES,
    ...FOOTPRINT_EFFECT_FRAMES,
    ...FOOTPRINT_PRINT_FRAMES.map((name) => `${name}_glow`),
  ],
};

// Цвет остывающего металла: от углей до белого каления. Опорные цвета разложены на мелкие
// шаги: на редких шагах цвет остывающего следа заметно скачет, и след рябит
const HEAT_ANCHORS = [
  0x2a1a10, 0x4a2410, 0x7a2e0c, 0xa83c08, 0xd4580a, 0xf07a10,
  0xf59a1c, 0xfbb72e, 0xffcf4a, 0xffe07e, 0xfff0b4, 0xffffff,
];
const HEAT_STEPS_PER_ANCHOR = 4;

const mixHeatChannel = (from, to, shift, share) => {
  const start = (from >> shift) & 0xff;
  return Math.round(start + (((to >> shift) & 0xff) - start) * share) << shift;
};

export const FOOTPRINT_HEAT_TINTS = HEAT_ANCHORS.slice(1).flatMap((anchor, index) => (
  Array.from({length: HEAT_STEPS_PER_ANCHOR}, (_, step) => {
    const share = step / HEAT_STEPS_PER_ANCHOR;
    const from = HEAT_ANCHORS[index];
    return mixHeatChannel(from, anchor, 16, share) + mixHeatChannel(from, anchor, 8, share) + mixHeatChannel(from, anchor, 0, share);
  })
)).concat(HEAT_ANCHORS[HEAT_ANCHORS.length - 1]);

const GOLD = 0xffcf4a;
const PALE_GOLD = 0xffe9a0;
const AMBER = 0xff9a1c;
const SCORCH = 0x1a0d05;

const D6 = UNIT_TYPES.D6.id;
const D8 = UNIT_TYPES.D8.id;
const D10 = UNIT_TYPES.D10.id;
const D12 = UNIT_TYPES.D12.id;
const D20 = UNIT_TYPES.D20.id;
const D4 = UNIT_TYPES.D4.id;

// Походка: как ложатся отпечатки вдоль пути
export const FOOTPRINT_GAITS = {
  // Левая, правая
  BIPED: 'biped',
  // Парами со сбивкой: заднее копыто почти наступает на след переднего
  QUADRUPED: 'quadruped',
  // Две сплошные колеи
  WHEELS: 'wheels',
};

// Сколько идут рядом: первая подходящая ступень сверху
export const FOOTPRINT_WALKER_STEPS = [
  {minSize: 12, count: 4},
  {minSize: 6, count: 3},
  {minSize: 3, count: 2},
  {minSize: 0, count: 1},
];

const FOOT_SHARED = {gait: FOOTPRINT_GAITS.BIPED, footGapFactor: 0.09};
// У лошади ноги расходятся шире шага бойца: заднее копыто ложится с другой стороны от
// линии хода. Один кавалерист идет шире (см. loneMountGapScale): иначе след вытянут в цепочку
const HOOF_SHARED = {gait: FOOTPRINT_GAITS.QUADRUPED, footGapFactor: 0.11};

/**
 * Сила следа по типу отряда. Доли — от размера ячейки.
 * printSizeFactor — размер отпечатка, strideFactor — длина шага, holdMs — сколько след
 * лежит после прихода отряда, restHeat — до какого накала садится отпечаток (0..1).
 * burnAlpha — золотое свечение вокруг отпечатка.
 * sparks, dust, motif, crack, ring, flash — раз в столько отпечатков (every) и сколько;
 * null — этого у типа нет.
 * aura — свет, который идет вместе с отрядом; rays — лучи вокруг него.
 * ribbon — огненная полоса вдоль пути: круги свечения через stepFactor и узкое ядро. Круг
 * занимает ячейку и больше, поэтому шаг держим редким, а плотность набираем прозрачностью:
 * наложения кругов — самая дорогая закраска следа на телефоне.
 * departure и arrival — всплеск на старте и удар о землю на приходе.
 */
export const FOOTPRINT_TIERS = {
  [D6]: {
    ...FOOT_SHARED,
    printSizeFactor: 0.38,
    strideFactor: 0.5,
    holdMs: 80,
    restHeat: 0.62,
    burnAlpha: 0.5,
    sparks: {every: 2, count: 1},
    dust: {every: 4, sizeFactor: 0.42, alpha: 0.26},
    motif: null,
    crack: null,
    ring: null,
    flash: {every: 2, sizeFactor: 0.5, alpha: 0.5},
    aura: {sizeFactor: 1.1, alpha: 0.26},
    rays: null,
    ribbon: null,
    departure: {sparkCount: 4, dustCount: 2, motifCount: 0, flashFactor: 0, hexRing: false, rays: false},
    arrival: {sparkCount: 6, dustCount: 2, motifCount: 0, flashFactor: 0.9, hexRing: false, rays: false, crack: false},
  },
  [D8]: {
    ...HOOF_SHARED,
    printSizeFactor: 0.36,
    strideFactor: 0.7,
    holdMs: 100,
    restHeat: 0.65,
    burnAlpha: 0.55,
    sparks: {every: 1, count: 1},
    dust: {every: 3, sizeFactor: 0.5, alpha: 0.32},
    motif: null,
    crack: null,
    ring: null,
    flash: {every: 2, sizeFactor: 0.5, alpha: 0.55},
    aura: {sizeFactor: 1.25, alpha: 0.3},
    rays: null,
    ribbon: {widthFactor: 0.34, alpha: 0.14, stepFactor: 0.2, coreAlpha: 0.2, coreStretch: 1},
    departure: {sparkCount: 6, dustCount: 4, motifCount: 0, flashFactor: 0.9, hexRing: false, rays: false},
    arrival: {sparkCount: 8, dustCount: 3, motifCount: 0, flashFactor: 1, hexRing: false, rays: false, crack: false},
  },
  [D10]: {
    ...FOOT_SHARED,
    printSizeFactor: 0.44,
    strideFactor: 0.54,
    holdMs: 140,
    restHeat: 0.68,
    burnAlpha: 0.62,
    sparks: {every: 1, count: 2},
    dust: {every: 3, sizeFactor: 0.5, alpha: 0.3},
    motif: {every: 4, count: 1},
    crack: null,
    ring: null,
    flash: {every: 1, sizeFactor: 0.6, alpha: 0.6},
    aura: {sizeFactor: 1.5, alpha: 0.38},
    rays: null,
    ribbon: {widthFactor: 0.42, alpha: 0.26, stepFactor: 0.26, coreAlpha: 0.25, coreStretch: 1.45},
    departure: {sparkCount: 8, dustCount: 3, motifCount: 2, flashFactor: 1.1, hexRing: true, rays: false},
    arrival: {sparkCount: 12, dustCount: 4, motifCount: 3, flashFactor: 1.3, hexRing: true, rays: false, crack: false},
  },
  [D12]: {
    ...HOOF_SHARED,
    printSizeFactor: 0.44,
    strideFactor: 0.76,
    holdMs: 200,
    restHeat: 0.72,
    burnAlpha: 0.7,
    sparks: {every: 1, count: 2},
    dust: {every: 2, sizeFactor: 0.62, alpha: 0.38},
    motif: {every: 3, count: 1},
    crack: {every: 6, sizeFactor: 0.7, alpha: 0.5},
    ring: {every: 8, toFactor: 0.9},
    flash: {every: 1, sizeFactor: 0.7, alpha: 0.68},
    aura: {sizeFactor: 1.8, alpha: 0.46},
    rays: {sizeFactor: 1.7, alpha: 0.26},
    ribbon: {widthFactor: 0.6, alpha: 0.46, stepFactor: 0.34, coreAlpha: 0.34, coreStretch: 2.1},
    departure: {sparkCount: 12, dustCount: 5, motifCount: 4, flashFactor: 1.3, hexRing: true, rays: true},
    arrival: {sparkCount: 16, dustCount: 6, motifCount: 5, flashFactor: 1.6, hexRing: true, rays: true, crack: true},
  },
  [D20]: {
    ...FOOT_SHARED,
    footGapFactor: 0.11,
    printSizeFactor: 0.54,
    strideFactor: 0.66,
    holdMs: 300,
    restHeat: 0.78,
    burnAlpha: 0.8,
    sparks: {every: 1, count: 3},
    dust: {every: 2, sizeFactor: 0.66, alpha: 0.38},
    motif: {every: 2, count: 2},
    crack: {every: 4, sizeFactor: 0.9, alpha: 0.56},
    ring: {every: 5, toFactor: 1.1},
    flash: {every: 1, sizeFactor: 0.86, alpha: 0.78},
    aura: {sizeFactor: 2.3, alpha: 0.56},
    rays: {sizeFactor: 2.3, alpha: 0.36},
    ribbon: {widthFactor: 0.86, alpha: 0.66, stepFactor: 0.36, coreAlpha: 0.42, coreStretch: 2.6},
    departure: {sparkCount: 18, dustCount: 7, motifCount: 8, flashFactor: 1.7, hexRing: true, rays: true},
    arrival: {sparkCount: 26, dustCount: 9, motifCount: 12, flashFactor: 2.1, hexRing: true, rays: true, crack: true},
  },
  // Катапульта катится: след — колея, искры из-под колес и пыль, сила не растет
  [D4]: {
    gait: FOOTPRINT_GAITS.WHEELS,
    footGapFactor: 0.2,
    printSizeFactor: 0.4,
    strideFactor: 0.27,
    holdMs: 100,
    restHeat: 0.62,
    burnAlpha: 0.42,
    sparks: {every: 2, count: 1},
    dust: {every: 3, sizeFactor: 0.46, alpha: 0.32},
    motif: null,
    crack: null,
    ring: null,
    flash: null,
    aura: {sizeFactor: 1.1, alpha: 0.24},
    rays: null,
    ribbon: null,
    departure: {sparkCount: 3, dustCount: 3, motifCount: 0, flashFactor: 0, hexRing: false, rays: false},
    arrival: {sparkCount: 4, dustCount: 3, motifCount: 0, flashFactor: 0, hexRing: false, rays: false, crack: false},
  },
};

// Частицы фракций в своих цветах: они дают следу характер, раз сам он у всех золотой.
// motion — физика материала (см. UNIT_FOOTPRINTS.particles): тяжелое падает, вода брызжет,
// огонь идет вверх, лист планирует. Физика следует из того, что это за предмет
const FIRE = {frame: 'tongue', tint: 0xff6a18, motion: 'flame'};
const HOLY_LIGHT = {frame: 'streak', tint: 0xfff2c4, motion: 'ascend', sizeScale: 1.7};
const SPELL = {frame: 'streak', tint: 0x8fc0ff, motion: 'ascend', sizeScale: 1.7};
const LEAF = {frame: 'leaf', tint: 0x86d94a, motion: 'flutter'};
const CLOVER = {frame: 'clover', tint: 0x62d659, motion: 'flutter'};
const COIN = {frame: 'coin', tint: 0xf8cf4a, motion: 'coin'};
const BONE = {frame: 'bone', tint: 0xf0e8cf, motion: 'bone'};
const SPORE = {frame: 'spore', tint: 0xb8d84e, motion: 'puff'};
// Чумное зловоние крысолюдов: мутно-зеленые клубы, ползут низко и рассеиваются
const PLAGUE = [
  {frame: 'miasma_a', tint: 0x7fa52e, motion: 'puff', sizeScale: 1.75},
  {frame: 'miasma_b', tint: 0x93b83a, motion: 'puff', sizeScale: 1.55},
  {frame: 'miasma_a', tint: 0x5f8a2a, motion: 'puff', sizeScale: 1.4},
];
// Сердце суккуба демоническое: с рожками, густого фиолетового цвета
const HEART = {frame: 'heart', tint: 0x9b30ff, motion: 'rise', sizeScale: 1.25};
const WATER = {frame: 'drop', tint: 0x55d0f0, motion: 'drop'};
const ACID = {frame: 'drop', tint: 0xb2e640, motion: 'drop'};
const debris = (frame, tint) => ({frame, tint, motion: 'debris'});
// Шевроны людей: знак звания, строгий и ровный. Идет прямо вверх, острием вперед
const RANK_CHEVRONS = [
  {frame: 'chevron', tint: 0xffc53a, motion: 'ascend'},
  {frame: 'chevron', tint: 0xff8f1f, motion: 'ascend'},
];
// Кузня: камень и раскаленный металл
const FORGE = [debris('chip', 0xb0a590), debris('chip', 0xff9a2c)];
const GOBLIN_SCRAP = [debris('chip', 0x8a6a3a), debris('splinter', 0xa8c040)];
// Обломки агрессивных фракций: из-под ног назад летят осколки, куски земли и кости
const ORC_DEBRIS = [debris('splinter', 0xe8351f), debris('chip', 0x94602f), debris('fang', 0xf2e8cf)];
const BEAST_DEBRIS = [debris('fang', 0xf4ead2), debris('chip', 0x8a5a2c), debris('splinter', 0xd64a1e)];
const wisp = (tint) => ({frame: 'wisp', tint, motion: 'rise'});

/**
 * Вид следа по облику фракции.
 * foot — ноги пехоты; mount — легкая кавалерия; heavyMount — тяжелая (нет — как у
 * легкой); hero — элитная пехота (нет — как foot). Список кадров: отпечаток выбирается
 * случайно на каждый ряд.
 * motif — что вылетает из-под ног (список — случайно на каждую частицу); tail — волочится
 * хвост; isSingleBody — идет один, и с численностью растет отпечаток, а не число рядов.
 * isViolent — агрессивная фракция: искры злее, на земле борозды когтей.
 * heroPrintScale — поправка размера у элиты: рисунок бывает слишком массивным.
 * isEarthshaker — от шага дрожит земля: разлом, низкая пыль, волна, а у очень крупного
 * отряда и камера (см. UNIT_FOOTPRINTS.quake).
 */
export const FOOTPRINT_FACTION_STYLES = {
  [FACTIONS.HUMANS]: {foot: 'boot', hero: 'sabaton', mount: 'horseshoe', heavyMount: 'war_shoe', motif: RANK_CHEVRONS},
  [FACTIONS.ORCS]: {foot: 'orc_foot', mount: 'wolf_paw', heavyMount: 'boar', motif: ORC_DEBRIS, isViolent: true, heroPrintScale: 0.78},
  [FACTIONS.DWARVES]: {foot: 'hobnail', mount: 'horseshoe', heavyMount: 'war_shoe', motif: FORGE},
  [FACTIONS.VENTORY]: {foot: 'sabaton', mount: 'horseshoe', heavyMount: 'war_shoe', motif: COIN},
  [FACTIONS.LEPRECHAUNS]: {foot: 'curly', mount: 'horseshoe', heavyMount: 'war_shoe', motif: [COIN, CLOVER]},
  [FACTIONS.DEMONS]: {foot: 'demon_talon', mount: 'hell_shoe', motif: FIRE},
  [FACTIONS.BEASTMEN]: {foot: 'cloven', mount: 'boar', motif: BEAST_DEBRIS, isViolent: true},
  [FACTIONS.ELEMENTALS]: {foot: 'flame', mount: 'flame_shoe', motif: FIRE},
  // Эльфы в латных башмаках, как Вентори: босая ступня у пехоты выглядела комично
  [FACTIONS.ELVES]: {foot: 'sabaton', mount: 'horseshoe', heavyMount: 'horseshoe', motif: LEAF},
  // Просветленные тоже: решение владельца
  [FACTIONS.ENLIGHTENED]: {foot: 'sabaton', mount: 'horseshoe', heavyMount: 'war_shoe', motif: HOLY_LIGHT},
  // Великан ничего не разбрасывает: от его шага дрожит сама земля (см. quake)
  [FACTIONS.GIANTS]: {foot: 'giant', mount: 'giant', isSingleBody: true, isEarthshaker: true},
  [FACTIONS.INSECTOIDS]: {foot: 'spike', mount: 'spike', motif: ACID},
  [FACTIONS.GOBLINS]: {foot: 'goblin_foot', mount: 'wolf_paw', heavyMount: 'boar', motif: GOBLIN_SCRAP},
  [FACTIONS.RATMEN]: {foot: 'rat_paw', mount: 'rat_paw', motif: PLAGUE, tail: true},
  [FACTIONS.SUCCUBES]: {foot: 'stiletto', mount: 'hell_shoe', motif: HEART},
  [FACTIONS.CELECTIALS]: {foot: 'bare', mount: 'horseshoe', heavyMount: 'war_shoe', motif: HOLY_LIGHT},
  [FACTIONS.AQUARS]: {foot: 'webbed', mount: 'webbed', motif: WATER},
  [FACTIONS.DARK_ELVES]: {foot: 'leaf_sole', mount: 'talon', motif: wisp(0xa86cf0)},
  [FACTIONS.TECHNODWARVES]: {foot: 'hobnail', mount: 'war_shoe', motif: FORGE},
  [FACTIONS.DRAGONS]: {foot: 'dragon_talon', mount: 'dragon_talon', motif: FIRE},
  [FACTIONS.ENTS]: {foot: 'root', mount: 'wolf_paw', heavyMount: 'root', motif: LEAF},
  [FACTIONS.FENIX]: {foot: 'talon', mount: 'flame_shoe', motif: FIRE},
  [FACTIONS.GREMLINS]: {foot: 'goblin_foot', mount: 'war_shoe', motif: FORGE},
  [FACTIONS.MUSHROOM_PEOPLE]: {foot: 'blob', mount: 'blob', motif: SPORE},
  [FACTIONS.SPIRITS]: {foot: 'wisp', mount: 'wisp', motif: wisp(0xdcecff)},
  // Допплер меняет облик на ходу: отпечатки чужие и у каждого в ряду свои
  [FACTIONS.DOPPLERS]: {
    foot: ['boot', 'talon', 'cloven', 'bare', 'rat_paw'],
    mount: ['horseshoe', 'cloven', 'wolf_paw'],
    motif: wisp(0xf070c0),
  },
  [FACTIONS.UNDEAD]: {foot: 'bone_foot', mount: 'horseshoe', heavyMount: 'war_shoe', motif: BONE},
  [FACTIONS.WIZARDS]: {foot: 'boot', mount: 'horseshoe', heavyMount: 'war_shoe', motif: SPELL},
};

// Отряд общим силуэтом: образа нет, отряд нейтральный. Своих частиц нет, только искры
export const FOOTPRINT_DEFAULT_STYLE = {foot: 'boot', mount: 'horseshoe', heavyMount: 'horseshoe'};

export const UNIT_FOOTPRINTS = {
  colors: {gold: GOLD, paleGold: PALE_GOLD, amber: AMBER, scorch: SCORCH},
  print: {
    // Отпечаток вдавливается: начинает чуть крупнее и садится на место
    popMs: 130,
    popFromScale: 1.18,
    // За столько белое каление садится на рабочий накал
    settleMs: 260,
    // Доля остывания, с которой отпечаток уже начинает исчезать
    vanishFromShare: 0.5,
    fadeMs: 620,
  },
  // Остывание идет от начала пути к концу: столько идет его фронт. Начинается почти сразу
  // после прихода отряда (holdMs): след живет чуть больше секунды и не висит на поле
  fadeSweepMs: 420,
  // Подпалина: темный размытый силуэт. Отделяет след от цветных жетонов и светлой земли
  scorch: {sizeFactor: 1.26, alpha: 0.62},
  // Золотое свечение вокруг отпечатка: обычным смешиванием, сложение на песке и снеге
  // выгорает в белое (то же решение у крыльев)
  burn: {sizeFactor: 1.5},
  flash: {lifeMs: 300, fromScale: 0.8, toScale: 1.25},
  // Отпечатки не под самой иконкой на старте и не под ней на приходе, доли ячейки
  startInsetFactor: 0.12,
  endInsetFactor: 0.22,
  // Расстояние между рядами и шум, доли ячейки
  laneGapFactor: 0.36,
  // Чем больше рядов, тем реже шаг в каждом: иначе след сливается в кашу
  laneStrideGrow: 0.12,
  laneJitterFactor: 0.012,
  strideJitter: 0.1,
  angleJitter: 0.09,
  // Путь идет ломаной по центрам ячеек, а след сглаживает изломы: и направление, и
  // место берутся по точкам вокруг шага
  tangentReachFactor: 0.42,
  // Отставание шагов у идущих в разных рядах, доли шага
  laneStaggerShare: 0.37,
  hindLagShare: 0.36,
  tailEvery: 2,
  // Во сколько раз шире идет кавалерист, когда он один в ряду
  loneMountGapScale: 1.7,
  // На какой стороне от линии хода заднее копыто: с другой, чуть ближе к оси
  hindGapShare: 0.75,
  // Рост единственного тела с численностью
  singleBodyGrow: {perSize: 0.035, max: 1.7},
  // Чтобы не тратить кадр на телефоне: при превышении шаг следа удлиняется, а лишние
  // частицы не появляются. На отпечаток уходит три спрайта
  maxPrintsPerTrail: 42,
  maxSparksPerTrail: 70,
  maxLiveTrails: 6,
  maxStepMs: 50,
  // Искры — черточки вдоль полета: резко вылетают во все стороны и падают. Обычным
  // смешиванием: так они видны и на снегу
  spark: {
    lifeMs: 420,
    lifeSpread: 0.45,
    sizeFactor: 0.27,
    sizeSpread: 0.6,
    reachFactor: 0.63,
    riseFactor: 0.62,
    gravityFactor: 0.9,
    burstReachFactor: 1.42,
  },
  dust: {lifeMs: 560, riseFactor: 0.2, driftFactor: 0.22, growTo: 1.9, tint: 0xc9a56a},
  // ФИЗИКА ЧАСТИЦ ПО МАТЕРИАЛУ. stepShare — сколько на один выброс под ногой, burstShare —
  // доля от всплеска на старте и на приходе. Всплеск — не фонтан из одной точки: тяжелое
  // разлетается низко по земле, легкое возникает по всей ячейке
  particles: {
    // Обломок вышиблен с силой: сразу на полной скорости, с вращением, падает и лежит
    debris: {
      lifeMs: 560, lifeSpread: 0.3, landShare: 0.62, sizeFactor: 0.32, sizeSpread: 0.7, reachFactor: 0.72,
      burstReachFactor: 1.15, liftFactor: 0.34, coneRadians: 1.05, spin: 9, stepShare: 2, burstShare: 2,
    },
    // Монета тяжелая: летит невысоко, вертится ребром, отскакивает от земли и лежит лицом вверх
    coin: {
      lifeMs: 820, lifeSpread: 0.2, landShare: 0.46, sizeFactor: 0.25, sizeSpread: 0.25, reachFactor: 0.6,
      burstReachFactor: 1.05, liftFactor: 0.46, coneRadians: 1.3, spin: 0, flips: 3.5, bounce: 0.34, stepShare: 1, burstShare: 1,
    },
    // Кость кувыркается и подскакивает с глухим стуком: тяжелее и ниже монеты
    bone: {
      lifeMs: 680, lifeSpread: 0.25, landShare: 0.5, sizeFactor: 0.3, sizeSpread: 0.35, reachFactor: 0.62,
      burstReachFactor: 1.05, liftFactor: 0.36, coneRadians: 1.2, spin: 11, bounce: 0.26, stepShare: 1, burstShare: 1,
    },
    // Капля — брызги: быстрая, вытянута вдоль полета, на земле от нее расходится круг
    drop: {
      lifeMs: 400, lifeSpread: 0.3, landShare: 0.8, sizeFactor: 0.17, sizeSpread: 0.6, reachFactor: 0.66,
      burstReachFactor: 1.2, liftFactor: 0.24, coneRadians: 1.2, spin: 0, isAligned: true, stretch: 1.4, stepShare: 2, burstShare: 1.6,
      ripple: {chance: 0.6, sizeFactor: 0.42, lifeMs: 340, alpha: 0.7},
    },
    // Огонь идет только вверх: разгорается на месте, ускоряется и тает
    flame: {
      lifeMs: 460, lifeSpread: 0.3, sizeFactor: 0.38, sizeSpread: 0.5, riseFactor: 0.72, spreadFactor: 0.12, burstShare: 0.8, stepShare: 1,
    },
    // Лист сорван с земли: подлетает невысоко и планирует вниз, покачиваясь, а не вертясь
    flutter: {
      lifeMs: 860, lifeSpread: 0.25, sizeFactor: 0.26, sizeSpread: 0.4, driftFactor: 0.5, liftFactor: 0.2, fallFactor: 0.34,
      swayFactor: 0.12, swayTurns: 1.3, rock: 0.75, spreadFactor: 0.1, burstShare: 0.5, stepShare: 1,
    },
    // Легкое и бесплотное — свет, сердце, дух — всплывает прямо вверх и тает
    rise: {
      lifeMs: 720, lifeSpread: 0.25, sizeFactor: 0.25, sizeSpread: 0.35, riseFactor: 0.66, wobbleFactor: 0.04, spreadFactor: 0.14, burstShare: 0.5, stepShare: 1,
    },
    // Знак и луч света не имеют веса, но и не парят: идут строго вверх по прямой, без качки
    ascend: {
      lifeMs: 640, lifeSpread: 0.2, sizeFactor: 0.25, sizeSpread: 0.25, riseFactor: 0.8, wobbleFactor: 0, spreadFactor: 0.14, burstShare: 0.5, stepShare: 1,
    },
    // Дым и споры расползаются клубом, чуть поднимаются, медленно поворачиваются и редеют
    puff: {
      lifeMs: 860, lifeSpread: 0.25, sizeFactor: 0.3, sizeSpread: 0.4, driftFactor: 0.24, riseFactor: 0.14, turn: 0.5, growTo: 2, alpha: 0.72, spreadFactor: 0.18, burstShare: 0.6, stepShare: 1,
    },
    // Всплеск легких частиц растянут по времени: они возникают одна за другой
    burstScatterFactor: 0.72,
    burstDelayMs: 180,
    // Доля срока на отскок: у монеты и кости
    hopShare: 0.24,
    hopTravel: 0.14,
  },
  // Шаг, от которого дрожит земля (великаны). Ничего не летит: тяжесть видна в том, что
  // происходит с землей. Камера дрожит на каждом шаге, начиная с fromSize: чуть заметно, а
  // к fullSize сильнее (для сравнения: взрыв у постройки дает 0.0025–0.004)
  quake: {
    // Разлом — тонкая темная трещина в земле, без огня. Заметно шире ступни: иначе его
    // закрывает сам отпечаток
    crackSizeFactor: 0.98,
    crackAlpha: 0.85,
    // Пыль стелется низко и расходится от ступни во все стороны
    skirtCount: 5,
    skirtSizeFactor: 0.5,
    skirtAlpha: 0.42,
    skirtReachFactor: 0.62,
    // Волна идет по земле: сплюснута, как круг на поверхности, и темнее нее
    waveSizeFactor: 1.5,
    waveSquash: 0.62,
    waveAlpha: 0.55,
    waveTint: 0x2a1c10,
    waveLifeMs: 420,
    camera: {
      fromSize: 21,
      fullSize: 99,
      minIntensity: 0.0009,
      maxIntensity: 0.0055,
      minDurationMs: 110,
      maxDurationMs: 210,
      // Последний шаг, на приходе, тяжелее обычного
      arrivalScale: 1.35,
    },
  },
  // Борозды когтей агрессивных фракций: реже обломков, вдоль хода
  gouge: {sizeFactor: 0.78, alpha: 0.62, every: 2},
  // Искры агрессивной фракции: цвет злее
  violentSparkTints: [0xff4a14, 0xff7a1c, 0xffb03a],
  ring: {lifeMs: 480, fromFactor: 0.25, alpha: 0.85},
  crack: {growMs: 130, tint: 0x120904, glowTint: AMBER},
  ribbon: {appearMs: 160, coreShare: 0.36},
  // Свет вокруг идущего отряда
  aura: {appearMs: 130, fadeMs: 320, turn: 1.6},
  // Удар о землю на приходе и всплеск на старте
  burst: {
    spreadFactor: 0.34,
    dustSizeFactor: 0.72,
    dustAlpha: 0.42,
    flashSizeFactor: 1.5,
    flashLifeMs: 340,
    flashAlpha: 0.9,
    raysSizeFactor: 2.6,
    raysLifeMs: 480,
    raysAlpha: 0.72,
    hexRingToFactor: 2.5,
    hexRingDelaysMs: [0, 110],
    hexRingLifeMs: 560,
    crackSizeFactor: 1.5,
    crackAlpha: 0.66,
  },
};
